/**
 * Read-only contact list.
 *
 * Renders contacts as a list of cards with a click-to-call telephone link and a
 * copy button. Shared by the retrieval success state and the dashboard preview,
 * so the two present recovered contacts identically.
 *
 * Telephone numbers appear here because the whole point of a successful retrieval
 * is to show them. They are still never placed in a URL query, logged, or written
 * to storage; a `tel:` link is a user-initiated action, not stored state.
 */

import { el, icon } from "./dom.js";

/**
 * @param {import("../types/models.js").Contact[]} contacts
 * @returns {HTMLElement}
 */
export function renderContactList(contacts) {
  if (contacts.length === 0) {
    return el("p", { class: "kin-note" }, ["No contacts to show."]);
  }

  return el(
    "ul",
    { class: "kin-contact-list" },
    contacts.map((contact) => el("li", {}, [renderContactCard(contact)])),
  );
}

/**
 * @param {import("../types/models.js").Contact} contact
 * @returns {HTMLElement}
 */
function renderContactCard(contact) {
  const telHref = `tel:${contact.telephone.replace(/\s+/g, "")}`;

  return el("wa-card", { class: "kin-contact-card" }, [
    el("div", { class: "kin-contact-head" }, [
      el("span", { class: "kin-contact-name" }, [contact.name]),
      contact.relationship ? el("wa-badge", { variant: "neutral" }, [contact.relationship]) : null,
    ]),
    el("div", { class: "kin-contact-actions" }, [
      el("a", { href: telHref, class: "kin-contact-tel" }, [
        icon("phone"),
        el("span", {}, [contact.telephone]),
      ]),
      el("wa-copy-button", {
        value: contact.telephone,
        "copy-label": `Copy number for ${contact.name}`,
      }),
    ]),
  ]);
}
